<template>
  <Modal
    size="md"
    @close="emit('deselect')"
  >
    <template #header>
      {{ $t('Notifications.contractEventPopup.title') }}
    </template>

    <template #body>
      <AppSpinner
        v-if="fetchContractEventAction.is(States.LOADING)"
        class="mx-auto my-8"
      />

      <template v-else-if="contractEvent">
        <div class="space-y-4">
          <div class="flex justify-between items-center">
            <p
              v-if="!isPending"
              :class="['text-sm rounded-lg border px-3 py-2', ContractEventStatusBadgeClasses[enumKeyFromValue(ContractEventStatus, contractEvent.status)]]"
            >
              {{ $t('Notifications.contractEventPopup.alreadyProcessed') }} - {{ ContractEventStatusTitles[enumKeyFromValue(ContractEventStatus, contractEvent.status)] }}
            </p>

            <button
              type="button"
              class="btn btn--secondary-blue"
              @click="toggleAttachments"
            >
              {{ showAttachments ? $t('Notifications.contractEventPopup.viewDetails') : $t('Notifications.contractEventPopup.viewAttachments') }}
            </button>
          </div>

          <dl
            v-if="!showAttachments"
            class="space-y-3 text-sm"
          >
            <div>
              <dt class="font-medium text-gray-500">
                {{ $t('Notifications.contractEventPopup.project') }}
              </dt>

              <dd class="mt-0.5 text-gray-900">
                {{ project?.name }}
              </dd>
            </div>

            <div>
              <dt class="font-medium text-gray-500">
                {{ $t('Notifications.contractEventPopup.name') }}
              </dt>

              <dd class="mt-0.5 text-gray-900">
                {{ contractEvent.name }}
              </dd>
            </div>

            <div>
              <dt class="font-medium text-gray-500">
                {{ $t('Notifications.contractEventPopup.dateOfOccurrence') }}
              </dt>

              <dd class="mt-0.5 text-gray-900">
                {{ contractEvent.date_of_occurrence ? formatDate(contractEvent.date_of_occurrence) : '—' }}
              </dd>
            </div>

            <div v-if="contractEvent.user">
              <dt class="font-medium text-gray-500">
                {{ $t('Notifications.contractEventPopup.raisedBy') }}
              </dt>

              <dd class="mt-0.5 text-gray-900">
                {{ contractEvent.user.name }}
              </dd>
            </div>
          </dl>

          <div
            v-else
            class="flex"
          >
            <AppSpinner
              v-if="contractEventMediaCollection.mediaRequestAction.is(States.LOADING)"
              class="mx-auto my-8"
            />

            <div
              v-else-if="contractEventMediaCollection.mediaCollection.value.length === 0"
              class="text-sm text-gray-500 text-center mx-auto"
            >
              {{ $t('Notifications.contractEventPopup.noAttachmentsFound') }}
            </div>

            <div
              v-else
              class="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <MediaDisplay
                v-for="media in contractEventMediaCollection.mediaCollection.value"
                :key="media.uuid"
                :media="media"
                :allow-delete="false"
                class="w-full"
              />
            </div>
          </div>
        </div>
      </template>
    </template>

    <template #footer>
      <div class="flex flex-wrap gap-2 justify-between">
        <button
          type="button"
          class="btn btn--tertiary-blue"
          @click="emit('deselect')"
        >
          Close
        </button>

        <RouterLink
          v-if="userStore.hasAccessToRoute('CenSchedule') && contractEvent"
          class="btn btn--secondary-blue"
          :to="cenScheduleRoute"
          @click="emit('deselect')"
        >
          {{ $t('Notifications.contractEventPopup.viewCenSchedule') }}
        </RouterLink>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import MediaDisplay from '@/components/MediaDisplay.vue';
import Modal from '@/components/Modal.vue';
import { useMediaCollection } from '@/composables/useMediaCollection';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { enumKeyFromValue } from '@/helpers';
import { t } from '@/i18n';
import {
  ContractEventStatus,
  ContractEventStatusBadgeClasses,
  ContractEventStatusTitles,
} from '@/models/ContractEvent';
import { useContractEventsStore } from '@/store/contractEvents';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  notification: AppNotification;
}>();

const emit = defineEmits<{
  (e: 'deselect');
  (e: 'reload');
}>();

const contractEventsStore = useContractEventsStore();
const projectsStore = useProjectsStore();
const userStore = useUserStore();

const contractEvent = computed(() => {
  return contractEventsStore.models.find(parseInt(props.notification.data.contract_event_id.toString()));
});

const fetchContractEventAction = useStoreApiAction(contractEventsStore.fetchContractEvent);

const project = computed(() => {
  return projectsStore.models.find(parseInt(props.notification.data.project_id.toString()));
});

watch(() => props.notification.data.contract_event_id, (newValue) => {
  if(newValue) {
    fetchContractEventAction.request(newValue);
  }
}, { immediate: true });

const isPending = computed(() => {
  const s = contractEvent.value?.status;

  return s === ContractEventStatus.pending;
});

const showAttachments = ref(false);
const hasLoadedMedia = ref(false);
const contractEventMediaCollection = useMediaCollection(contractEventsStore.media);

const toggleAttachments = () => {
  showAttachments.value = !showAttachments.value;

  if(showAttachments.value && !hasLoadedMedia.value && contractEvent.value?.id) {
    contractEventMediaCollection.clear();

    contractEventMediaCollection.fetchMedia(contractEvent.value.id).then(() => {
      hasLoadedMedia.value = true;
    }).catch(() => {
      Swal.fire({
        icon: 'error',
        title: t('Notifications.contractEventPopup.actionFailed'),
      });

      showAttachments.value = false;
    });
  }
};

function formatDate(dateString: string): string {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED);
}

const cenScheduleRoute = computed(() => ({
  name: 'CenSchedule',
  params: {
    projectId: props.notification.data.project_id.toString(),
    cenId: contractEvent.value?.id.toString(),
  },
}));
</script>
